/**
 * Pure encode/parse logic for the command-center deep-link state — the slice
 * of view state `/dashboard` persists in the URL so a reload (or a shared
 * link) restores the same screen, mode, filter and scope.
 *
 * Same design as `communicatorDeepLink` / `groupDeepLink`: decoupled from
 * React and react-router so round-trip, totality and the legacy migrations can
 * be property-tested in isolation.
 *
 * Conventions:
 *   - Every field omits its default from the encoded params, so a bare
 *     `/dashboard` IS the canonical "my tasks, list, no filter" URL.
 *   - Parsing is **total**: each field falls back to its own default, so
 *     {@link parse} never throws.
 *   - Two LEGACY views are migrated on parse, not by rewriting the URL:
 *     `?view=calendar` becomes `view=tasks&taskView=calendar`, and
 *     `?view=groups` keeps working as-is. Doing it with a `history.replace`
 *     on mount would make Back to a legacy link appear stuck, since the
 *     rewrite would fire again immediately.
 *   - `?view=comms` (ТЗ 0096) — экрана «Общение» больше нет. Разбор читает
 *     его как задачи, а страница по {@link legacyCommsRedirect} уводит такую
 *     ссылку в Коммуникатор на ту же группу. Это ДРУГОЙ маршрут, и уход
 *     через `replace` не залипает: «Назад» ведёт мимо старой ссылки.
 */

import { PATHS } from 'router/config';
import { communicatorLink } from 'core/utils/communicatorDeepLink';
import {
  TASK_VIEWS,
  TASK_SORT_KEYS,
  TTaskSortKey,
  TTaskView,
} from 'pages/_detailShared/TaskBoard/tasksViewModel';

/** Top-level screen. `groups` is the secondary "обзор групп". */
export type TDashboardView = 'tasks' | 'groups';
/** Whose tasks: the personal to-do or the whole team. */
export type TDashboardTaskMode = 'mine' | 'team';
/** How the tasks are shown. Единственный источник — `tasksViewModel`;
 *  второй литерал здесь дал бы кнопку, чей URL при перезагрузке сбрасывается. */
export type TDashboardTaskView = TTaskView;
/** Toolbar chip. */
export type TDashboardTaskFilter = 'all' | 'today' | 'overdue' | 'noDate';
export type TDashboardSortDir = 'asc' | 'desc';

export interface IDashboardDeepLinkState {
  view: TDashboardView;
  taskMode: TDashboardTaskMode;
  taskView: TDashboardTaskView;
  taskFilter: TDashboardTaskFilter;
  /** Sort key + direction, one param (`due:asc`) so clearing it is one action. */
  taskSortKey: TTaskSortKey;
  taskSortDir: TDashboardSortDir;
  /** Наборы чекбоксов; в URL — списками через запятую. */
  taskStatuses: string[];
  /** Приоритеты (`normal` / `high`; ТЗ 0111 вернуло приоритет). */
  taskPriorities: string[];
  /** Assignee filter (staff user ids), team mode only. */
  assignees: string[];
  dueFrom: string;
  dueTo: string;
  withClosed: boolean;
  fromMessageOnly: boolean;
  subjectKind: string;
  /** Связанные сущности (ТЗ 0064). В URL — `kind:id` через запятую, потому
   *  что фильтр обязан переживать пересылку ссылки, как и все остальные. */
  links: string[];
  /** Selected group id — survives every view. Default `''`. */
  group: string;
  /** Поиск ЗАДАЧ (не рейки — у той свой `q`). Default `''`. */
  taskQuery: string;
  /** Rail search. Default `''`. */
  query: string;
  /**
   * Открытая карточка задачи (ТЗ 0068). Дип-линк из колокольчика ведёт не
   * «на доску», а В КОНКРЕТНУЮ задачу — иначе уведомление обязывает искать
   * её глазами в списке, ради которого оно и пришло.
   *
   * Живёт в URL, а не в состоянии компонента, потому что должно переживать
   * перезагрузку и пересылку ссылки, как и все остальные поля здесь.
   */
  task: string;
}

const PARAM = {
  view: 'view',
  taskMode: 'taskMode',
  taskView: 'taskView',
  taskFilter: 'taskFilter',
  taskSort: 'taskSort',
  taskStatuses: 'taskStatus',
  // ТЗ 0111: приоритет вернули — `taskPriority` снова читается.
  taskPriorities: 'taskPriority',
  // Имя параметра оставлено в единственном числе: старые ссылки
  // `?assignee=3` продолжают работать, просто читаются как список из одного.
  assignees: 'assignee',
  dueFrom: 'dueFrom',
  dueTo: 'dueTo',
  withClosed: 'withClosed',
  fromMessageOnly: 'fromMsg',
  subjectKind: 'subject',
  links: 'links',
  group: 'group',
  taskQuery: 'taskQ',
  query: 'q',
  task: 'task',
} as const;

const DEFAULT_VIEW: TDashboardView = 'tasks';
const DEFAULT_MODE: TDashboardTaskMode = 'mine';

/**
 * Стартовый вид ЗАВИСИТ ОТ ОБЛАСТИ (ТЗ 0065).
 *
 * До этого он был один на оба режима — доска сроков. Для «Контроля команды»
 * это верно: руководителю нужно видеть, что горит. Для «Моих задач» — нет:
 * свой список ведут по состоянию работы («что я делаю прямо сейчас»), а не по
 * календарю, и человек первым делом переключался на «Статусы».
 *
 * 🔴 Функция, а не две константы: её зовут И разбор, И кодирование. Кодировщик
 * опускает `taskView`, когда он равен дефолту, разбор подставляет его обратно —
 * если стороны разойдутся, круговой прогон `parse(encode(x)) === x` перестанет
 * сходиться, и ссылка начнёт менять экран при перезагрузке.
 */
export const defaultTaskView = (mode: TDashboardTaskMode): TDashboardTaskView =>
  mode === 'team' ? 'time' : 'status';

/** Вид для встроенных досок, у которых режима нет вовсе (вкладка студента,
 *  группы, преподавателя). */
export const DEFAULT_TASK_VIEW: TDashboardTaskView = defaultTaskView(
  DEFAULT_MODE,
);
const DEFAULT_FILTER: TDashboardTaskFilter = 'all';

const VIEWS: readonly TDashboardView[] = ['tasks', 'groups'];
const MODES: readonly TDashboardTaskMode[] = ['mine', 'team'];
const SORT_DIRS: readonly TDashboardSortDir[] = ['asc', 'desc'];
const DEFAULT_SORT_KEY: TTaskSortKey = 'manual';
const DEFAULT_SORT_DIR: TDashboardSortDir = 'asc';
const FILTERS: readonly TDashboardTaskFilter[] = [
  'all',
  'today',
  'overdue',
  'noDate',
];

/** The pre-rework value that is no longer a top-level view. */
const LEGACY_CALENDAR = 'calendar';
/** Бывший экран «Общение» (до ТЗ 0096) — теперь это Коммуникатор. */
const LEGACY_COMMS = 'comms';

/** Список через запятую; пустые токены выбрасываются, порядок не важен —
 *  `normalize` всё равно приведёт его к каноническому. */
const list = (raw: string | null): string[] =>
  (raw ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

/** Токен связи в URL — `kind:id`, оба непустые.
 *
 *  Вид НЕ сверяется со списком: список приезжает с сервера и зависит от роли,
 *  а проверять форму — работа парсера. Неизвестный вид сервер и так молча
 *  игнорирует, ровно как неизвестный статус. */
const isLinkToken = (raw: string): boolean => {
  const [kind, id] = raw.split(':');
  return Boolean(kind && id);
};

const oneOf = <T extends string>(
  values: readonly T[],
  raw: string | null,
  fallback: T,
): T =>
  raw !== null && (values as readonly string[]).includes(raw)
    ? (raw as T)
    : fallback;

/**
 * Enforce the state invariants:
 *   1. Task-only fields are meaningless outside the tasks view — they reset.
 *   2. The personal mode is implicitly "assigned to me", so an explicit
 *      assignee filter cannot coexist with it.
 *   3. `group` survives every view: tasks filters by it and the groups
 *      overview highlights it.
 */
export const normalize = (
  state: IDashboardDeepLinkState,
): IDashboardDeepLinkState => {
  const onTasks = state.view === 'tasks';
  const taskMode = onTasks ? state.taskMode : DEFAULT_MODE;
  const taskView = onTasks ? state.taskView : defaultTaskView(taskMode);
  const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).sort();

  let dueFrom = onTasks ? state.dueFrom : '';
  let dueTo = onTasks ? state.dueTo : '';
  // Перепутанный местами диапазон — не ошибка разбора, а опечатка в ссылке;
  // меняем концы, иначе экран пуст без всякого объяснения.
  if (dueFrom && dueTo && dueFrom > dueTo) [dueFrom, dueTo] = [dueTo, dueFrom];

  // Чип «без срока» и диапазон дат взаимоисключающи: серверный диапазон
  // ИСКЛЮЧАЕТ задачи без срока, вместе они всегда дают ноль.
  const hasRange = Boolean(dueFrom || dueTo);
  let taskFilter = onTasks ? state.taskFilter : DEFAULT_FILTER;
  if (hasRange && taskFilter === 'noDate') taskFilter = DEFAULT_FILTER;

  // Ни календарь, ни Гант не потребляют порядок — две ссылки, дающие один и
  // тот же экран, обязаны кодироваться одинаково.
  const sortable = onTasks && taskView !== 'calendar' && taskView !== 'gantt';
  const taskSortKey = sortable ? state.taskSortKey : DEFAULT_SORT_KEY;
  const taskSortDir =
    taskSortKey === DEFAULT_SORT_KEY ? DEFAULT_SORT_DIR : state.taskSortDir;

  return {
    view: state.view,
    taskMode,
    taskView,
    taskFilter,
    taskSortKey,
    taskSortDir,
    taskStatuses: onTasks ? uniq(state.taskStatuses) : [],
    taskPriorities: onTasks ? uniq(state.taskPriorities) : [],
    // Личный режим и есть «назначено мне»; явный исполнитель был бы вторым,
    // противоречащим ответом на тот же вопрос.
    assignees: onTasks && taskMode === 'team' ? uniq(state.assignees) : [],
    dueFrom,
    dueTo,
    withClosed: onTasks ? state.withClosed : false,
    fromMessageOnly: onTasks ? state.fromMessageOnly : false,
    subjectKind: onTasks ? state.subjectKind : '',
    // Формат `kind:id`; мусор выброшен здесь, а не на сервере, чтобы
    // одна протухшая закладка не обнуляла экран.
    links: onTasks ? uniq(state.links).filter(isLinkToken) : [],
    group: state.group,
    taskQuery: onTasks ? state.taskQuery : '',
    query: state.query,
    // Карточка задачи существует только на экране задач: `?view=groups&task=7`
    // открыл бы её поверх обзора групп, где ей нечего делать.
    task: onTasks ? state.task : '',
  };
};

/**
 * Encode a state into {@link URLSearchParams}, omitting every default.
 * `parse(encode(state))` restores an equal state for any normalized input.
 */
export const encode = (state: IDashboardDeepLinkState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.view !== DEFAULT_VIEW) params.set(PARAM.view, state.view);
  if (state.taskMode !== DEFAULT_MODE) {
    params.set(PARAM.taskMode, state.taskMode);
  }
  // Дефолт зависит от режима, и режим уже закодирован выше — разбор восстановит
  // ту же пару.
  if (state.taskView !== defaultTaskView(state.taskMode)) {
    params.set(PARAM.taskView, state.taskView);
  }
  if (state.taskFilter !== DEFAULT_FILTER) {
    params.set(PARAM.taskFilter, state.taskFilter);
  }
  if (state.taskSortKey !== DEFAULT_SORT_KEY) {
    params.set(PARAM.taskSort, `${state.taskSortKey}:${state.taskSortDir}`);
  }
  if (state.taskStatuses.length) {
    params.set(PARAM.taskStatuses, state.taskStatuses.join(','));
  }
  if (state.taskPriorities.length) {
    params.set(PARAM.taskPriorities, state.taskPriorities.join(','));
  }
  if (state.links.length) {
    params.set(PARAM.links, state.links.join(','));
  }
  if (state.assignees.length) {
    params.set(PARAM.assignees, state.assignees.join(','));
  }
  if (state.dueFrom) params.set(PARAM.dueFrom, state.dueFrom);
  if (state.dueTo) params.set(PARAM.dueTo, state.dueTo);
  if (state.withClosed) params.set(PARAM.withClosed, '1');
  if (state.fromMessageOnly) params.set(PARAM.fromMessageOnly, '1');
  if (state.subjectKind) params.set(PARAM.subjectKind, state.subjectKind);
  if (state.group) params.set(PARAM.group, state.group);
  if (state.taskQuery) params.set(PARAM.taskQuery, state.taskQuery);
  if (state.query) params.set(PARAM.query, state.query);
  if (state.task) params.set(PARAM.task, state.task);

  return params;
};

/**
 * Parse a query string into a normalized state. Total and defensive: never
 * throws, every field falls back to its default on unrecognized input.
 */
export const parse = (
  search: string | URLSearchParams,
): IDashboardDeepLinkState => {
  const params = new URLSearchParams(search);
  const rawView = params.get(PARAM.view);
  const [rawSortKey, rawSortDir] = (params.get(PARAM.taskSort) ?? '').split(
    ':',
  );

  // Legacy `?view=calendar` — the calendar is a way of showing tasks now.
  const migratedCalendar = rawView === LEGACY_CALENDAR;
  const taskMode = oneOf(MODES, params.get(PARAM.taskMode), DEFAULT_MODE);

  return normalize({
    view: migratedCalendar ? 'tasks' : oneOf(VIEWS, rawView, DEFAULT_VIEW),
    taskMode,
    taskView: migratedCalendar
      ? 'calendar'
      : oneOf(
          TASK_VIEWS,
          params.get(PARAM.taskView),
          defaultTaskView(taskMode),
        ),
    taskFilter: oneOf(FILTERS, params.get(PARAM.taskFilter), DEFAULT_FILTER),
    taskSortKey: oneOf(TASK_SORT_KEYS, rawSortKey, DEFAULT_SORT_KEY),
    taskSortDir: oneOf(SORT_DIRS, rawSortDir, DEFAULT_SORT_DIR),
    taskStatuses: list(params.get(PARAM.taskStatuses)),
    taskPriorities: list(params.get(PARAM.taskPriorities)),
    links: list(params.get(PARAM.links)).filter(isLinkToken),
    assignees: list(params.get(PARAM.assignees)),
    dueFrom: params.get(PARAM.dueFrom) ?? '',
    dueTo: params.get(PARAM.dueTo) ?? '',
    withClosed: params.get(PARAM.withClosed) === '1',
    fromMessageOnly: params.get(PARAM.fromMessageOnly) === '1',
    subjectKind: params.get(PARAM.subjectKind) ?? '',
    group: params.get(PARAM.group) ?? '',
    taskQuery: params.get(PARAM.taskQuery) ?? '',
    query: params.get(PARAM.query) ?? '',
    task: params.get(PARAM.task) ?? '',
  });
};

/**
 * Ссылка на дашборд с точечной правкой состояния (ТЗ 0068).
 *
 * Адрес берётся из `PATHS`, а не собирается шаблонной строкой: путь,
 * записанный литералом, даёт 404, невидимый ни для `tsc`, ни для сборки, ни
 * для тестов — в этом репозитории так уже ломались ссылки на карточки.
 */
export const dashboardLink = (
  patch: Partial<IDashboardDeepLinkState>,
): string => {
  const params = encode(normalize({ ...parse(''), ...patch }));
  const qs = params.toString();
  return qs ? `${PATHS.dashboard}?${qs}` : PATHS.dashboard;
};

/**
 * Старая ссылка на «Общение» (`?view=comms[&group=X][&q=…]`, ТЗ 0096): куда
 * её увести. `null` — ссылка не старая, редирект не нужен.
 */
export const legacyCommsRedirect = (
  search: string | URLSearchParams,
): string | null => {
  const params = new URLSearchParams(search);
  if (params.get(PARAM.view) !== LEGACY_COMMS) return null;
  return communicatorLink({
    group: params.get(PARAM.group) ?? '',
    query: params.get(PARAM.query) ?? '',
  });
};
